import { validateCompositionRoute, validateCompositionLineageRoute } from './compositionRoute.js';
import { PlatformError, type DocStorePort, type DocTxOps, type DocTxRunner } from '@djgg/core';
import { canonicalDigest, canonicalJson } from '@djgg/gameplay-kit';
import { lockCompositionJobs, lockCompositionMediaPolicies, lockCompositionVersions, type CompositionMediaPolicyLocks } from './compositionMediaPolicy.js';
import { assetGenJobDoc, assetVariantDoc, buildJobDoc, versionDoc } from '../paths.js';
import { compositionMediaExecutionDigest, compositionReceiptCoversV1, validateAcceptedCompositionV1, validateCompositionMediaReceiptImageV1, validateCompositionMediaReceiptMusicV1, type AcceptedCompositionV1, type CompositionMediaReceiptV1 } from '../spec/gameSpec.js';
import { compositionBundleSlotsV1, compositionMediaSlotKindV1 } from '../spec/compositionPresentation.js';
import type { AssetGenJob, AssetVariantDoc, BuildJob, CompositionFinalProofV1, CreationVersion } from '../types.js';
import type { CommitCompositionProofInput } from './compositionProofBarrier.js';
import { compositionMediaResumeJournal } from './compositionMediaResume.js';

/** Both final-proof sealing and atomic publication read the exact child and
 * immutable variants under their transaction. Mutable library selection is
 * never final-media authority. Lock order follows job → sorted creation owners →
 * version → child → variants → rejection records, shared with publication. */
export async function assertCompositionMediaReceipt(tx: DocTxOps, job: BuildJob, version: CreationVersion,
  authority: AcceptedCompositionV1, receipt: CompositionMediaReceiptV1, expectedJobId: string,
  policies?: CompositionMediaPolicyLocks): Promise<void> {
  const barrier = job.measuredExecution?.compositionBarrier;
  const visual = version.creationRun?.visualJobId;
  const lineage = await validateCompositionLineageRoute(job,version,expectedJobId);
  if (!authority.media || !barrier || (!lineage && !visual) || version.creationRun?.musicJobId
    || canonicalJson(barrier.mediaJobIds) !== canonicalJson(lineage ? [] : [visual])
    || !receipt || receipt.schemaVersion !== 2 || receipt.authorityDigest !== authority.digest
    || receipt.closureDigest !== authority.closureDigest || receipt.mediaBasisDigest !== barrier.mediaBasisDigest
    || receipt.structuralSourceDigest !== (barrier.structural.candidate.report.compositionProof as { sourceDigest?: string } | undefined)?.sourceDigest
    || !compositionReceiptCoversV1(receipt, authority.media.slots, authority.spec)) {
    throw new PlatformError('conflict','Final composition media basis or required slots changed');
  }
  // The media journal: the child plus failed exact-game predecessors across
  // runtime-only successors (whose accepted slots it reused).
  const journal = new Set<string>();
  // A revision's carried slots bind its predecessor's published variants,
  // exactly as its accepted carry names them (the child published none).
  const carried = new Map((policies?.jobs.revision?.carry ?? []).map(item => [item.slot, item.from]));
  if (!!version.executionPolicy?.compositionRevision !== !!policies?.jobs.revision) {
    throw new PlatformError('conflict','Final composition media revision was not locked');
  }
  if (!lineage) {
    const mediaExecution = authority.media.execution;
    const mediaExecutionId = mediaExecution ? await compositionMediaExecutionDigest(mediaExecution) : undefined;
    const child = await tx.get<AssetGenJob>(assetGenJobDoc(visual!));
    if (!child || child.status !== 'succeeded' || child.creationId !== job.creationId
      || child.creationRun?.version !== job.version || child.creationRun.branch !== 'visual'
      || child.prerequisite?.kind !== 'composition-proof' || child.prerequisite.authorityDigest !== authority.digest
      || child.prerequisite.mediaBasisDigest !== barrier.mediaBasisDigest || child.prerequisite.releasedAt !== barrier.committedAt
      || !child.measuredBudget || child.measuredBudget.buildJobId !== version.creationRun?.codeJobId
      || child.measuredBudget.acceptedAt !== job.measuredExecution!.acceptedAt
      || child.measuredBudget.deadlineAt !== job.measuredExecution!.deadlineAt
      || child.measuredBudget.role !== mediaExecution?.role
      || child.measuredBudget.authorityDigest !== (mediaExecution ? authority.digest : undefined)
      || child.measuredBudget.executionDigest !== mediaExecutionId
      || canonicalJson([...(child.plan?.assetNames ?? [])].sort()) !== canonicalJson([...authority.media.slots].sort())
      // The deferred strips are exactly the ones the child recorded deferring.
      || canonicalJson(receipt.deferred ?? []) !== canonicalJson(child.deferredSlots ?? [])) {
      throw new PlatformError('conflict','Final composition media child is incomplete or changed');
    }
    try {
      for (const id of await compositionMediaResumeJournal({
        getMediaJob: id => tx.get<AssetGenJob>(assetGenJobDoc(id)),
        getBuildJob: id => tx.get<BuildJob>(buildJobDoc(id)),
        getVersion: (creationId, version) => tx.get<CreationVersion>(versionDoc(creationId, version)),
      }, {...child,id:visual!},authority)) journal.add(id);
    } catch {
      throw new PlatformError('conflict','Final composition media journal changed');
    }
  } else {
    // A successor binds exactly its lineage refs; every other bundle slot is
    // one it inherits as deferred (compositionReceiptCoversV1 holds the
    // deferrable-only rule).
    const refs = lineage.media.kind === 'coded' ? [] : lineage.media.refs.map(ref => ref.slot);
    if (canonicalJson((receipt.deferred ?? []).map(item => item.slot))
      !== canonicalJson(compositionBundleSlotsV1(authority.media.slots).filter(slot => !refs.includes(slot)).sort())) {
      throw new PlatformError('conflict','Final composition media deferral differs from its lineage');
    }
  }
  const bound = [...receipt.images.map(image => ({ image, music: undefined })),
    ...(receipt.music ? [{ image: undefined, music: receipt.music }] : [])];
  for (const { image: picture, music } of bound.sort((a,b) => (a.image ?? a.music)!.slot.localeCompare((b.image ?? b.music)!.slot))) {
    const image = (picture ?? music)!;
    try {
      if (picture) validateCompositionMediaReceiptImageV1(picture, authority.media.slots);
      else validateCompositionMediaReceiptMusicV1(music!, authority.media.slots);
    } catch { throw new PlatformError('conflict','Invalid final media receipt'); }
    const carry = carried.get(image.slot);
    if ((!lineage && !carry && !journal.has(image.jobId ?? '')) || !image.variantId) throw new PlatformError('conflict','Invalid final media receipt');
    const ref = lineage && lineage.media.kind !== 'coded' ? lineage.media.refs.find(ref=>ref.slot===image.slot) : carry;
    if ((lineage || carry) && (!ref || ref.variantId!==image.variantId || ref.contentSha256!==image.contentSha256)) {
      throw new PlatformError('conflict','Final composition media differs from accepted successor');
    }
    const namespace=ref?.creationId ?? job.creationId;
    const variant = await tx.get<AssetVariantDoc>(assetVariantDoc(namespace,image.variantId));
    if (!variant || variant.variantId !== image.variantId || variant.creationId !== namespace
      || (variant.genJobId ?? null) !== (image.jobId ?? null)
      || ref && variant.url !== ref.url
      // A carried variant is bound by its identity, not its name (a revision
      // may have renamed the kind, an asset be named apart from its variant);
      // every other is named by its own slot.
      || (!carry && variant.name !== image.slot) || variant.kind !== compositionMediaSlotKindV1(image.slot)
      // A pinned carry/successor ref already binds both URL and digest. Legacy
      // creator variants may predate the optional stored digest; generated
      // media without a ref must still carry its own exact variant digest.
      || (variant.contentSha256 ?? ref?.contentSha256) !== image.contentSha256
      || (lineage ? variant.exposureState != null && variant.exposureState !== 'active'
        : variant.exposureState !== 'active')
      || (carry && variant.reviewState === 'superseded')
      || variant.reviewState === 'rejected'
      // Bound geometry/metadata is the variant's own, never re-derived.
      || canonicalJson(picture?.strip ?? null) !== canonicalJson(variant.strip
        ? { frames: variant.strip.frames, frameWidth: variant.strip.frameWidth, frameHeight: variant.strip.frameHeight } : null)
      || (music && canonicalJson(music.audio) !== canonicalJson(variant.audio ?? null))) {
      throw new PlatformError('conflict','Final composition variant changed or is unavailable');
    }
    if (ref) {
      if (!policies?.revisions.has(namespace)) throw new Error('Composition media policy was not locked');
      // The verified receipt is the resolved byte identity. Legacy variant rows
      // may predate the optional stored digest, while their pinned refs do not.
      await policies.policy.assertAllowed(namespace,image.contentSha256,policies.revisions.get(namespace)!,tx);
    }
  }
}

export async function commitCompositionFinalProof(store: DocStorePort & Partial<DocTxRunner>, now: () => number,
  input: CommitCompositionProofInput): Promise<CompositionFinalProofV1> {
  if (!store.runTransaction) throw new PlatformError('unavailable','Final composition proof requires transactions');
  const hintJob = await store.getDoc<BuildJob>(buildJobDoc(input.jobId));
  const hint = hintJob ? await store.getDoc<CreationVersion>(versionDoc(hintJob.creationId,hintJob.version)) : null;
  if (!hint) throw new PlatformError('conflict','Composition version unavailable');
  return store.runTransaction(async tx => {
    const jobLocks = await lockCompositionJobs(tx,input.jobId,hint);
    const job = jobLocks.job, run = job.measuredExecution;
    if (!job || job.status !== 'claimed' || !input.claim.claimToken || job.workerId !== input.claim.workerId
      || job.claimToken !== input.claim.claimToken || job.leaseGeneration !== input.claim.leaseGeneration
      || (job.leaseExpiry ?? 0) <= now() || !run || now() >= run.deadlineAt-run.limits.finishReserveMs) {
      throw new PlatformError('conflict','Final composition proof lost its active claim or deadline');
    }
    const policies = await lockCompositionMediaPolicies(store,tx,jobLocks);
    const version = (await lockCompositionVersions(tx,jobLocks)).target;
    if (!version?.executionPolicy?.composition || version.pipelineSnapshot?.id !== 'measured-first-playable' || ![4, 5].includes(version.pipelineSnapshot.revision) || (!['generating','gating'].includes(version.buildStatus) && !(version.buildStatus === 'publishing' && run.compositionFinalProof))) throw new PlatformError('conflict','Final composition version unavailable');
    const authority = await validateAcceptedCompositionV1(version.executionPolicy.composition);
    await validateCompositionRoute(job, version, input.jobId);
    const barrier = run.compositionBarrier, checkpoint = run.checkpoint, candidate = checkpoint?.candidates[input.candidateId];
    const report = candidate?.report, proof = report?.compositionProof as CreationVersion['gates']['compositionProof'];
    const checks = report?.checks as Record<string,{ran:boolean;passed:boolean|null;enforced:boolean}> | undefined;
    if (![4, 5].includes(run.recipe.revision) || run.compositionDigest !== authority.digest || !barrier
      || barrier.authorityDigest !== authority.digest
      || barrier.structural.candidateDigest !== await canonicalDigest(barrier.structural.candidate)
      || barrier.proofDigest !== await canonicalDigest(barrier.structural.candidate.report.compositionProof)
      || !checkpoint || checkpoint.revision !== input.checkpointRevision || checkpoint.revision <= barrier.checkpointRevision
      || checkpoint.currentCandidateId !== input.candidateId || checkpoint.bestCandidateId !== input.candidateId
      || input.candidateId === barrier.candidateId || !candidate?.runnable || !candidate.dist || report?.ok !== true
      || !['typecheck','tests','build'].every(key => (report[key] as {ok?:boolean})?.ok === true)
      || candidate.remainingChecks.some(key => checks?.[key]?.enforced !== false)
      || checks?.['composition-native-loop']?.ran !== true || checks['composition-native-loop'].passed !== true
      || !proof || proof.authorityDigest !== authority.digest || proof.closureDigest !== authority.closureDigest
      || ![proof.sourceDigest,proof.distDigest,proof.nativeDigest,proof.resolutionDigest].every(value => /^[a-f0-9]{64}$/.test(value))) {
      throw new PlatformError('conflict','Final composition proof is incomplete or stale');
    }
    const receipt = report.compositionMedia as CompositionMediaReceiptV1;
    await assertCompositionMediaReceipt(tx,job,version,authority,receipt,input.jobId,policies);
    const identity = {schemaVersion:1 as const,authorityDigest:authority.digest,structuralProofDigest:barrier.proofDigest,
      mediaBasisDigest:barrier.mediaBasisDigest,checkpointRevision:checkpoint.revision,candidateId:input.candidateId,
      candidateDigest:await canonicalDigest(candidate),candidate:structuredClone(candidate),receipt:structuredClone(receipt),receiptDigest:await canonicalDigest(receipt)};
    if (run.compositionFinalProof) {
      const {committedAt:_time,claimGeneration:_generation,...prior} = run.compositionFinalProof;
      if (canonicalJson(prior) !== canonicalJson(identity)) throw new PlatformError('conflict','Final composition proof identity changed');
      return structuredClone(run.compositionFinalProof);
    }
    const final = {...identity,committedAt:now(),claimGeneration:job.leaseGeneration!};
    await tx.update(buildJobDoc(input.jobId),{measuredExecution:{...run,ledgerRevision:(run.ledgerRevision??0)+1,compositionFinalProof:final}});
    return final;
  });
}
